<template>
  <!--
    The must-read gate. A PrimeVue Dialog with every dismissal path switched
    off: no close icon, no escape, no dismissable mask. That keeps the screen
    inside the design system (and inside the school's own theme) while still
    leaving the acknowledge button as the only way out, which is the whole
    point of the flag.
  -->
  <Dialog :visible="!!current" modal :closable="false" :close-on-escape="false" :dismissable-mask="false"
    :draggable="false" :style="{ width: '38rem' }" :breakpoints="{ '640px': '95vw' }"
    :aria-label="`Required reading: ${current?.title ?? ''}`">
    <template #header>
      <div class="flex items-start gap-3" data-testid="announcement-gate">
        <i :class="['pi text-xl mt-0.5', style.icon]" />
        <div class="min-w-0">
          <div class="flex flex-wrap items-center gap-2">
            <Tag :value="style.label" :severity="style.severity" />
            <span class="text-xs font-medium text-muted-color">Required reading</span>
          </div>
          <h2 class="mt-1.5 text-lg font-semibold leading-tight" data-testid="gate-title">
            {{ current?.title }}
          </h2>
          <p class="mt-0.5 text-xs text-muted-color">{{ metaLine }}</p>
        </div>
      </div>
    </template>

    <!-- Scrolls inside the dialog so a long notice never pushes the
         acknowledge button out of reach. -->
    <div class="max-h-[45vh] overflow-y-auto" data-testid="gate-body">
      <p class="whitespace-pre-line text-sm leading-relaxed">{{ current?.body }}</p>
    </div>

    <Message v-if="failed" severity="error" :closable="false" class="mt-3" data-testid="gate-error">
      That didn't save — check your connection and try again.
    </Message>

    <template #footer>
      <div class="flex w-full flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <span class="text-xs text-muted-color" data-testid="gate-progress">{{ progressLabel }}</span>
        <Button :label="acknowledging ? 'Saving…' : 'I have read this'" icon="pi pi-check"
          :loading="acknowledging" data-testid="gate-acknowledge" @click="onAcknowledge" />
      </div>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { usePendingAnnouncements, severityStyle } from '@/composables/useSchoolAnnouncements';

/**
 * Blocks the student portal until every must-read notice is acknowledged.
 *
 * The queue is server-derived: acknowledging refetches rather than splicing
 * locally, so a notice that expired or was archived mid-session stops blocking
 * on the next step instead of being enforced from a stale list.
 */
const { pending, acknowledging, acknowledge } = usePendingAnnouncements();

const failed = ref(false);

const current = computed(() => pending.value[0] ?? null);
const style = computed(() => severityStyle(current.value?.severity));

const total = computed(() => pending.value.length);
const progressLabel = computed(() => (total.value > 1
  ? `${total.value} notices need your attention`
  : 'You can browse the app once you have read this'));

const metaLine = computed(() => {
  const notice = current.value;
  if (!notice) return '';
  const who = notice.createdBy?.name ? `Posted by ${notice.createdBy.name}` : 'Posted by your school';
  return notice.audience_label ? `${who} · ${notice.audience_label}` : who;
});

const onAcknowledge = async() => {
  const notice = current.value;
  if (!notice) return;
  failed.value = !(await acknowledge(notice));
};
</script>
